import {
  PartnerReferralChannel,
  PartnerReferralItemResponse,
  PartnerReferralStatus,
} from '@interfaces/partners/responses/partner-referral.response';

/**
 * Funções puras do Indique e ganhe (card 3461): montagem do link e do compartilhamento, e os
 * textos de canal e status. Ficam fora do componente para ter spec isolado.
 */

/** Canais com botão de compartilhar. O link copiado puro não leva `?via=` e vira `Link`. */
export type ReferralShareChannel = 'whatsapp' | 'linkedin' | 'email';

const SHARE_SUBJECT = 'Convite para o portal de parceiros da Legatus';
const SHARE_MESSAGE =
  'Quero te convidar para ser parceiro da Legatus. Cadastre-se no portal de parceiros pelo meu link:';

/**
 * Link de indicação: `<partnerLandingUrl>/r/<código>`.
 *
 * Tira a barra final da base (o environment pode vir com ou sem) para não sair `//r/`.
 */
export function buildReferralLink(landingUrl: string, code: string): string {
  const base = (landingUrl ?? '').replace(/\/+$/, '');
  return `${base}/r/${encodeURIComponent(code)}`;
}

/** O mesmo link com o canal: `?via=whatsapp|linkedin|email`. É o que a landing grava no clique. */
export function buildReferralLinkWithChannel(link: string, channel: ReferralShareChannel): string {
  const separator = link.includes('?') ? '&' : '?';
  return `${link}${separator}via=${channel}`;
}

/** URL que abre o compartilhamento no canal escolhido, já com o link marcado com o canal. */
export function buildShareUrl(link: string, channel: ReferralShareChannel): string {
  const tracked = buildReferralLinkWithChannel(link, channel);

  switch (channel) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${SHARE_MESSAGE} ${tracked}`)}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(tracked)}`;
    case 'email':
      return `mailto:?subject=${encodeURIComponent(SHARE_SUBJECT)}&body=${encodeURIComponent(
        `${SHARE_MESSAGE}\n${tracked}`
      )}`;
  }
}

const CHANNEL_LABELS: Record<PartnerReferralChannel, string> = {
  Link: 'Link',
  WhatsApp: 'WhatsApp',
  LinkedIn: 'LinkedIn',
  Email: 'E-mail',
};

export function referralChannelLabel(channel: PartnerReferralChannel | null | undefined): string {
  return (channel && CHANNEL_LABELS[channel]) || 'Link';
}

const STATUS_LABELS: Record<PartnerReferralStatus, string> = {
  SignedUp: 'Parceiro cadastrado',
  DealGenerated: 'Já registrou deal',
};

export function referralStatusLabel(status: PartnerReferralStatus): string {
  return STATUS_LABELS[status] ?? status;
}

/** Linha de detalhe embaixo do parceiro indicado: quantos deals ele mesmo já registrou como parceiro. */
export function referralDetail(item: Pick<PartnerReferralItemResponse, 'dealsCount'>): string {
  const count = item.dealsCount ?? 0;
  if (count <= 0) return 'Cadastrado como parceiro, ainda sem deal registrado';
  return count === 1
    ? 'Já registrou 1 deal como parceiro'
    : `Já registrou ${count} deals como parceiro`;
}
